import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

// Pages that should be accessible even without completing onboarding
const ONBOARDING_EXEMPT_PATHS = ['/onboarding', '/settings', '/pricing'];

// Pages that require SUPER_ADMIN role
const SUPER_ADMIN_PATHS = ['/admin', '/governance', '/users'];

export const ProtectedRoute = ({ children, requiredRole = null }) => {
  const location = useLocation();
  const { 
    user, 
    isLoading, 
    isAuthenticated, 
    isInitialized,
    isSuperAdmin,
    hasRole 
  } = useAuth();

  // Debug log for troubleshooting
  console.log('[ProtectedRoute]', {
    path: location.pathname,
    isInitialized,
    isLoading,
    isAuthenticated,
    user: user?.email,
    roles: user?.roles,
    systemRole: user?.system_role,
    isSuperAdmin: user ? (user.roles?.includes('super_admin') || user.system_role === 'super_admin') : false,
    requiredRole
  });

  // Show loading spinner while auth is initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying session...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Direct check for super_admin role (more reliable than callback)
  const userIsSuperAdmin = user?.roles?.includes('super_admin') || user?.system_role === 'super_admin';

  // Check for required role
  if (requiredRole) {
    const userHasRole = user?.roles?.includes(requiredRole) || user?.system_role === requiredRole;
    if (!userHasRole) {
      console.log('[ProtectedRoute] Missing required role:', requiredRole);
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Check SUPER_ADMIN paths
  if (SUPER_ADMIN_PATHS.some(path => location.pathname.startsWith(path))) {
    if (!userIsSuperAdmin) {
      console.log('[ProtectedRoute] Super admin required for path:', location.pathname);
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Redirect to onboarding if not complete (and not on exempt path)
  if (user && !user.onboarding_complete && !ONBOARDING_EXEMPT_PATHS.includes(location.pathname)) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Higher-order component for role-protected routes
export const RequireSuperAdmin = ({ children }) => {
  return <ProtectedRoute requiredRole="super_admin">{children}</ProtectedRoute>;
};

export const RequireAdmin = ({ children }) => {
  return <ProtectedRoute requiredRole="admin">{children}</ProtectedRoute>;
};

export default ProtectedRoute;
